/**
 * Tag — generic intent-based label pill (scores, counts, BETA/SOON, overflow +N).
 *
 * Pill family (do not unify APIs — different semantics):
 *   Tag          → intent + variant (+ palette) — decorative / value labels
 *   StatusPill   → variant (status color) — job/candidate/interview/invitation state
 *   SkillChip    → tone — skill/language chips (wraps Tag; always enables `wrap`)
 *
 * Size floors (height grows when the label wraps):
 * sm min-h-5 · md min-h-6 (Figma default) · lg min-h-8 · xl min-h-10 (pairs with Button md)
 *
 * Layout:
 * - default — `w-fit`, single-line
 * - `wrap` — `w-fit` + `max-w-full`; hugs when short, wraps when the parent is narrower
 * - `fullWidth` — `w-full`; label centers; independent of `wrap`
 * Chrome (icon / close) stays on the outer nowrap row; only the label wraps.
 *
 * @example
 * <Tag intent="success">75%</Tag>
 * <Tag intent="primary" variant="outline" icon={<Check />}>Active</Tag>
 * <Tag intent="error" closable onClose={handleRemove} closeAriaLabel={t('...')}>Filter</Tag>
 * <Tag intent="secondary" variant="subtle" wrap>Oracle Cloud Infrastructure (OCI)</Tag>
 * <Tag intent="info" variant="subtle" fullWidth wrap>Section title</Tag>
 */

import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { IconWrapper } from '@/components/ui/atoms/IconWrapper';
import { closeButtonStyles, getVariantStyles, iconSizeMap, sizeStyles } from './Tag.styles';
import type { TagProps } from './Tag.types';

export const Tag: React.FC<TagProps> = ({
  children,
  intent,
  palette = 'standard',
  size = 'md',
  variant = 'solid',
  icon,
  pill = true,
  disabled = false,
  closable = false,
  onClose,
  closeAriaLabel = 'Remove',
  fullWidth = false,
  wrap = false,
  className,
  ...props
}) => (
  <span
    className={cn(
      'inline-flex max-w-full min-w-0 items-center justify-center self-start font-semibold font-body tracking-normal',
      pill ? 'rounded-full' : 'rounded',
      sizeStyles[size],
      getVariantStyles(variant, intent, palette),
      disabled && 'opacity-50 pointer-events-none cursor-not-allowed',
      !disabled && props.onClick && 'cursor-pointer',
      fullWidth ? 'w-full' : 'w-fit',
      wrap ? 'whitespace-normal' : 'shrink-0 whitespace-nowrap overflow-hidden',
      className
    )}
    aria-disabled={disabled}
    {...props}
  >
    {icon && (
      <IconWrapper size={iconSizeMap[size]} color="inherit" aria-hidden className="shrink-0">
        {icon}
      </IconWrapper>
    )}
    <span
      className={cn(
        '[&>svg]:mr-1 [&>svg]:inline [&>svg]:shrink-0 [&>svg]:align-middle',
        wrap ? 'min-w-0 max-w-full break-words' : 'whitespace-nowrap',
        fullWidth && 'flex-1 text-center',
        size === 'sm' && 'translate-y-px'
      )}
    >
      {children}
    </span>
    {closable && onClose && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose(e);
        }}
        className={cn(
          'cursor-pointer rounded-full hover:bg-dark-invirtus/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-dark-invirtus/20 shrink-0 grid place-items-center',
          closeButtonStyles[size]
        )}
        aria-label={closeAriaLabel}
      >
        <IconWrapper size={iconSizeMap[size]} color="inherit" aria-hidden>
          <X />
        </IconWrapper>
      </button>
    )}
  </span>
);

Tag.displayName = 'Tag';
